import React, { useState } from "react";
import styles from "../../styles/Template.module.css";
import { Button } from "../../components/Modal";
import { FormData } from "../../hooks/rtmTemplate/index.type";

interface TemplateTableProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  handleDeleteQuestion: (key: string) => void;
  handleEditQuestion: (key: string, name: string, sequence: number) => void;
  onSubmit?: () => void;
  isEditing?: boolean;
  handleToggleEditState?: () => void;
}

const CategoryTable: React.FC<TemplateTableProps> = ({
  formData,
  setFormData,
  handleDeleteQuestion,
  handleEditQuestion,
  isEditing = false,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const onDragStart =
    (index: number) => (event: React.DragEvent<HTMLTableRowElement>) => {
      setDraggedIndex(index);
      event.dataTransfer.effectAllowed = "move";
    };

  const onDragOver =
    (index: number) => (event: React.DragEvent<HTMLTableRowElement>) => {
      event.preventDefault();
      setDragOverIndex(index);
    };

  const onDrop =
    (index: number) => (event: React.DragEvent<HTMLTableRowElement>) => {
      event.preventDefault();
      if (draggedIndex === null) return;

      const newCategories = [...formData.categories];
      const draggedItem = newCategories[draggedIndex];
      newCategories.splice(draggedIndex, 1);
      newCategories.splice(index, 0, draggedItem);

      const updatedCategories = newCategories.map((cat, idx) => ({
        ...cat,
        sequence: idx + 1,
      }));

      setFormData((prev) => ({
        ...prev,
        categories: updatedCategories,
      }));

      setDraggedIndex(null);
      setDragOverIndex(null);
    };

  const onDragLeave =
    (index: number) => (event: React.DragEvent<HTMLTableRowElement>) => {
      setDragOverIndex(null);
    };

  const onDragEnd = (event: React.DragEvent<HTMLTableRowElement>) => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const sortedCategories = [...formData.categories].sort(
    (a, b) => Number(a.sequence) - Number(b.sequence)
  );

  return (
    <div className={styles.categoryWrapper} >
      <table className={styles.table}>
        <thead style={{width: '40rem'}}>
          <tr>
            <th>Category No.</th>
            <th >Category Name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedCategories.length > 0 ? (
            sortedCategories.map((row, index) => (
              <tr
                key={row.sequence}
                draggable
                onDragStart={(e) => {
                  const isButton = (e.target as HTMLElement).closest("button");
                  if (isButton) return;
                  onDragStart(index)(e);
                }}
                onDragOver={(e) => onDragOver(index)(e)}
                onDrop={(e) => onDrop(index)(e)}
                onDragLeave={(e) => onDragLeave(index)(e)}
                onDragEnd={(e) => onDragEnd(e)}
                style={{
                  cursor: "move",
                  opacity: draggedIndex === index ? 0.5 : 1,
                  backgroundColor:
                    dragOverIndex === index ? "#f5f5f5" : "transparent",
                }}
              >
                <td>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <span>{row.sequence}</span>
                  </div>
                </td>
                <td>{row.name}</td>
                <td style={{ paddingLeft: "0.5rem" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <button
                      onDragStart={(e) => e.stopPropagation()}
                      draggable={false}
                      className={styles.icon_button}
                      onClick={() =>
                        handleEditQuestion(
                          row.key,
                          row.name,
                          Number(row.sequence)
                        )
                      }
                      type="button"
                    >
                      <svg>
                        <use xlinkHref={`/assets/sprite.svg#icon-edit-new`} />
                      </svg>
                    </button>

                    {isEditing && (
                      <button
                        onDragStart={(e) => e.stopPropagation()}
                        draggable={false}
                        className={styles.icon_button}
                        onClick={() => handleDeleteQuestion(row.key)}
                        type="button"
                      >
                        <svg viewBox="0 0 16 12">
                          <path d="M4.225 0.553125L4 1H1C0.446875 1 0 1.44687 0 2C0 2.55312 0.446875 3 1 3H13C13.5531 3 14 2.55312 14 2C14 1.44687 13.5531 1 13 1H10L9.775 0.553125C9.60625 0.2125 9.25938 0 8.88125 0H5.11875C4.74062 0 4.39375 0.2125 4.225 0.553125ZM13 4H1L1.6625 14.5938C1.7125 15.3844 2.36875 16 3.15937 16H10.8406C11.6312 16 12.2875 15.3844 12.3375 14.5938L13 4Z" />
                        </svg>
                      </button>
                    )}

                    <button
                      onDragStart={(e) => e.stopPropagation()}
                      draggable={false}
                      className={styles.icon_grab}
                      type="button"
                      style={{ cursor: "move" }}
                    >
                      <svg width="16" height="18" viewBox="0 0 16 16">
                        <path
                          d="M2 8H14M2 4H14M2 12H14"
                          stroke="#1E1E1E"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={8} className={styles.noData_category}>
                No Category Added Yet
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CategoryTable;
